import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { Card, Btn, Modal, Empty } from "../../components/UI";
import { SlidePanel } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect } from "../engagement/FormControls";
import { LibraryImportWizard } from "../../components/LibraryImportWizard";
import { Plus, Upload, Pencil, Archive, RotateCcw, Search, BookOpen } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { code: "", title: "", category: "", status: "active", icd9: "", snomed: "", snomed_title: "" };
const FIELDS = [{ key: "code", label: "Diagnosis code", required: true }, { key: "title", label: "Title", required: true },
  { key: "category", label: "Category" }, { key: "status", label: "Status (active or inactive)" },
  { key: "icd9", label: "ICD-9 code" }, { key: "snomed", label: "SNOMED code" }, { key: "snomed_title", label: "SNOMED description" }];
const COLUMNS = [{ key: "code", label: "Code" }, { key: "title", label: "Title" }, { key: "icd9", label: "ICD-9" }, { key: "snomed", label: "SNOMED" }, { key: "category", label: "Category" }, { key: "status", label: "Status" }];
const TEMPLATE = "code,title,category,status,icd9,snomed,snomed description\nF32.1,\"Major depressive disorder, single episode, moderate\",Mood (affective) disorders,active,296.22,832007,\"Moderate major depression\"\n";
const FILTERS = [["current", "All current"], ["active", "Active"], ["inactive", "Inactive"], ["archived", "Archived"]];

function StatCards({ stats }) {
  const cards = [["Current entries", stats.total], ["Active", stats.active], ["Inactive", stats.inactive], ["Archived", stats.archived], ["Starter samples", stats.sample]];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="dxlib-stats">
      {cards.map(([k, v]) => (
        <div key={k} className="rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-wide text-[#7B6157]">{k}</div>
          <div className="font-heading text-2xl font-bold text-[#3E2723]" data-testid={`dxlib-stat-${k.toLowerCase().replace(/\s/g, "-")}`}>{v ?? 0}</div>
        </div>
      ))}
    </div>
  );
}

function EntryPanel({ entry, categories, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (entry) setForm({ ...EMPTY, ...entry }); }, [entry]);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (entry?.id) await api.put(`/diagnosis-library/${entry.id}`, form);
      else await api.post("/diagnosis-library", form);
      toast.success(entry?.id ? "Diagnosis updated" : "Diagnosis added to the library");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <SlidePanel open={!!entry} onClose={onClose} title={entry?.id ? "Edit library diagnosis" : "Add library diagnosis"} subtitle="Codes are normalized automatically, for example F321 becomes F32.1" testId="dxlib-panel">
      <form onSubmit={save} className="space-y-4">
        <div><FLabel>Code *</FLabel><FInput data-testid="dxlib-code" value={form.code} onChange={f("code")} placeholder="F32.1" /></div>
        <div><FLabel>Title *</FLabel><FInput data-testid="dxlib-title" value={form.title} onChange={f("title")} placeholder="Major depressive disorder, single episode, moderate" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><FLabel>ICD-9 code</FLabel><FInput data-testid="dxlib-icd9" value={form.icd9 || ""} onChange={f("icd9")} placeholder="296.22" /></div>
          <div><FLabel>SNOMED code</FLabel><FInput data-testid="dxlib-snomed" value={form.snomed || ""} onChange={f("snomed")} placeholder="832007" /></div>
        </div>
        <div><FLabel>SNOMED description</FLabel><FInput data-testid="dxlib-snomed-title" value={form.snomed_title || ""} onChange={f("snomed_title")} placeholder="Moderate major depression" /></div>
        <div><FLabel>Category</FLabel>
          <FInput data-testid="dxlib-category" list="dxlib-category-list" value={form.category} onChange={f("category")} placeholder="Leave blank to derive from the code" />
          <datalist id="dxlib-category-list">{categories.map((c) => <option key={c} value={c} />)}</datalist>
        </div>
        <div><FLabel>Status</FLabel>
          <FSelect data-testid="dxlib-status" value={form.status} onChange={f("status")}><option value="active">Active</option><option value="inactive">Inactive</option></FSelect>
        </div>
        <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn data-testid="dxlib-save" type="submit" disabled={busy || !form.code.trim() || !form.title.trim()}>{busy ? "Saving…" : "Save"}</Btn>
        </div>
      </form>
    </SlidePanel>
  );
}

function ArchiveEntryModal({ entry, onClose, onDone }) {
  const [reason, setReason] = useState("");
  useEffect(() => setReason(""), [entry]);
  const go = async () => {
    try { await api.post(`/diagnosis-library/${entry.id}/archive`, { reason }); toast.success("Diagnosis archived"); onDone(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal open={!!entry} onClose={onClose} title="Archive library diagnosis">
      <p className="text-sm text-[#5D4037]">Archive <strong>{entry?.code} {entry?.title}</strong>. Clinicians will no longer find it in the picker. Charts that already use it are not changed.</p>
      <textarea data-testid="dxlib-archive-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)"
        className="mt-3 w-full rounded-lg border border-[#E5DFD3] px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
      <div className="mt-4 flex justify-end gap-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn data-testid="dxlib-archive-confirm" disabled={reason.trim().length < 3} onClick={go}><Archive size={14} /> Archive</Btn></div>
    </Modal>
  );
}

export function ImportHistory({ refreshKey, kind = "diagnosis" }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/library-imports", { params: { kind } }).then((r) => setRows(r.data)).catch(() => {}); }, [refreshKey, kind]);
  if (!rows.length) return null;
  return (
    <Card className="p-5" data-testid={kind === "diagnosis" ? "dxlib-import-history" : "medlib-import-history"}>
      <h3 className="mb-3 font-heading text-base font-bold text-[#3E2723]">Import history</h3>
      <div className="space-y-2">
        {rows.map((b) => (
          <div key={b.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-[#FDFCF8] px-3 py-2 text-xs text-[#5D4037]" data-testid={`dxlib-import-${b.id}`}>
            <span className="font-semibold text-[#3E2723]">{b.filename}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${b.status === "committed" ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#F5F2EB] text-[#6D4C41]"}`}>{b.status === "committed" ? "committed" : b.status === "expired" ? "expired preview" : "preview only"}</span>
            <span>{b.counts?.total} rows</span>
            {b.result && <span>{b.result.inserted} added, {b.result.updated} updated, {b.result.skipped_invalid + b.result.skipped_duplicate} skipped</span>}
            <span className="ml-auto text-[#7B6157]">{b.created_by_name} · {dayjs(b.created_at).format("MMM D, YYYY h:mm A")}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function DiagnosisLibrarySection() {
  const [data, setData] = useState({ items: [], total: 0, stats: {} });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("current");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(null);
  const [archive, setArchive] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(() => {
    api.get("/diagnosis-library", { params: { q: q || undefined, status, category: category || undefined, page, size: 25 } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [q, status, category, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load, refreshKey]);
  useEffect(() => { api.get("/diagnosis-library/categories").then((r) => setCategories(r.data)).catch(() => {}); }, [refreshKey]);
  useEffect(() => { setPage(1); }, [q, status, category]);
  const refresh = () => setRefreshKey((k) => k + 1);
  const restore = async (row) => {
    try { await api.post(`/diagnosis-library/${row.id}/restore`); toast.success("Diagnosis restored"); refresh(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const pages = Math.max(1, Math.ceil(data.total / 25));

  return (
    <div className="space-y-5" data-testid="diagnosis-library-section">
      <Card className="p-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-[#3E2723]"><BookOpen size={18} /> Diagnosis Library</h2>
            <p className="mt-1 max-w-2xl text-sm text-[#6D4C41]">The clinic-wide diagnosis catalog clinicians search when charting. Import the QuickDoc export, add entries that are missing, and archive codes the clinic does not use.</p>
          </div>
          <div className="flex gap-2">
            <Btn variant="outline" data-testid="dxlib-import-btn" onClick={() => setImportOpen(true)}><Upload size={14} /> Import file</Btn>
            <Btn data-testid="dxlib-add-btn" onClick={() => setEdit({ ...EMPTY })}><Plus size={14} /> Add diagnosis</Btn>
          </div>
        </div>
        <StatCards stats={data.stats} />
        {data.stats.sample > 0 && (
          <div className="mt-4 rounded-xl border border-[#FFE0B2] bg-[#FFF8E1] px-4 py-2.5 text-xs text-[#8D5A00]" data-testid="dxlib-sample-banner">
            {data.stats.sample} entries are from the starter sample list. Importing the clinic file can archive them in the same step.
          </div>
        )}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-72">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7B6157]" />
            <input data-testid="dxlib-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ICD-10, ICD-9, SNOMED, or title"
              className="h-10 w-full rounded-full border border-[#E5DFD3] bg-white pl-9 pr-3 text-sm outline-none focus:border-[#8D6E63]" />
          </div>
          <div className="flex rounded-full bg-[#F5F2EB] p-1">
            {FILTERS.map(([k, l]) => (
              <button key={k} type="button" data-testid={`dxlib-filter-${k}`} onClick={() => setStatus(k)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${status === k ? "bg-[#4E342E] text-white" : "text-[#6D4C41]"}`}>{l}</button>
            ))}
          </div>
          <div className="w-full sm:w-72">
            <FSelect data-testid="dxlib-category-filter" value={category} placeholder="All categories" onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </FSelect>
          </div>
        </div>
        <div className="mt-4 overflow-x-auto rounded-2xl border border-[#EFEBE0]">
          {data.items.length === 0 ? <Empty text="No diagnoses match these filters" /> : (
            <table className="min-w-full text-sm" data-testid="dxlib-table">
              <thead className="bg-[#FDFCF8] text-left text-[10px] font-bold uppercase text-[#7B6157]">
                <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">ICD-9</th><th className="px-4 py-3">SNOMED</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Source</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {data.items.map((d) => (
                  <tr key={d.id} className="border-t border-[#EFEBE0] hover:bg-[#FDFCF8]" data-testid={`dxlib-row-${d.code}`}>
                    <td className="px-4 py-2 font-mono text-[13px] font-bold text-[#3E2723]">{d.code}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#6D4C41]" data-testid={`dxlib-icd9-${d.code}`}>{d.icd9 || "-"}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#6D4C41]" data-testid={`dxlib-snomed-${d.code}`} title={d.snomed_title || undefined}>{d.snomed || "-"}</td>
                    <td className="px-4 py-2 text-[#3E2723]">{d.title}{d.snomed_title && <div className="text-[10px] text-[#7B6157]">SNOMED: {d.snomed_title}</div>}</td>
                    <td className="px-4 py-2 text-xs text-[#7B6157]">{d.category}</td>
                    <td className="px-4 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${d.archived ? "bg-[#ECEFF1] text-[#546E7A]" : d.status === "active" ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#F5F2EB] text-[#6D4C41]"}`}>{d.archived ? "archived" : d.status}</span></td>
                    <td className="px-4 py-2 text-xs text-[#7B6157]">{d.is_sample ? "Starter sample" : d.source === "import" ? "Import" : "Added manually"}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-1">
                        {d.archived ? (
                          <button type="button" data-testid={`dxlib-restore-${d.code}`} onClick={() => restore(d)} title={`Archived: ${d.archive_reason || ""}`} className="flex h-8 items-center gap-1 rounded-full px-2 text-xs font-semibold text-[#4E342E] hover:bg-[#EDE7DC]"><RotateCcw size={13} /> Restore</button>
                        ) : (
                          <>
                            <button type="button" data-testid={`dxlib-edit-${d.code}`} aria-label="Edit" onClick={() => setEdit(d)} className="flex h-8 w-8 items-center justify-center rounded-full text-[#7B6157] hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                            <button type="button" data-testid={`dxlib-archive-${d.code}`} aria-label="Archive" onClick={() => setArchive(d)} className="flex h-8 w-8 items-center justify-center rounded-full text-[#7B6157] hover:bg-[#EDE7DC]"><Archive size={13} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex items-center justify-between border-t border-[#F1EEE7] px-4 py-2 text-xs text-[#7B6157]" data-testid="dxlib-pagination">
            <span>{data.total} result{data.total === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-1">
              <Btn variant="ghost" className="!px-3 !py-1 text-xs" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="dxlib-prev">Prev</Btn>
              <span>Page {page} of {pages}</span>
              <Btn variant="ghost" className="!px-3 !py-1 text-xs" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="dxlib-next">Next</Btn>
            </div>
          </div>
        </div>
      </Card>
      <ImportHistory refreshKey={refreshKey} />
      <EntryPanel entry={edit} categories={categories} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); refresh(); }} />
      <ArchiveEntryModal entry={archive} onClose={() => setArchive(null)} onDone={() => { setArchive(null); refresh(); }} />
      <LibraryImportWizard open={importOpen} onClose={() => setImportOpen(false)} onDone={refresh} entity="Diagnosis"
        previewUrl="/diagnosis-library/import/preview" commitUrl={(id) => `/diagnosis-library/import/${id}/commit`}
        fields={FIELDS} columns={COLUMNS} templateCsv={TEMPLATE} />
    </div>
  );
}
